import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    /** Optional custom fallback. When omitted, a friendly storefront-safe fallback is shown. */
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * App-root error boundary. Mounted per-portal in PortalProviders so any render-time
 * throw degrades to a friendly, self-contained fallback instead of white-screening the
 * live storefront. The fallback intentionally depends on NO app context/provider (inline
 * styles, native reload) so it still renders even if a context provider is what threw.
 */
export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Surface details in dev; in production avoid leaking stack traces to the console.
        if (import.meta.env.DEV) {
            console.error('Uncaught error:', error, errorInfo);
        }
    }

    private handleReset = () => {
        // Try a soft recovery first (re-render the tree). If the error is sticky, the
        // user can use the full reload button below.
        this.setState({ hasError: false, error: null });
    };

    private handleReload = () => {
        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div
                    role="alert"
                    style={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2rem',
                        background: '#0f172a',
                        color: '#f8fafc',
                        fontFamily:
                            'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                    }}
                >
                    <div
                        style={{
                            maxWidth: '32rem',
                            textAlign: 'center',
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '1rem',
                            padding: '2.5rem 2rem'
                        }}
                    >
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }} aria-hidden>
                            🌊
                        </div>
                        <h1 style={{ fontSize: '1.5rem', margin: '0 0 0.75rem' }}>
                            Something went wrong
                        </h1>
                        <p style={{ margin: '0 0 1.5rem', color: '#cbd5e1', lineHeight: 1.5 }}>
                            We hit an unexpected error while loading this page. Your data is safe — please
                            try again.
                        </p>
                        <div
                            style={{
                                display: 'flex',
                                gap: '0.75rem',
                                justifyContent: 'center',
                                flexWrap: 'wrap'
                            }}
                        >
                            <button
                                type="button"
                                onClick={this.handleReset}
                                style={{
                                    cursor: 'pointer',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    background: 'transparent',
                                    color: '#f8fafc',
                                    borderRadius: '0.5rem',
                                    padding: '0.625rem 1.25rem',
                                    fontSize: '0.95rem'
                                }}
                            >
                                Try again
                            </button>
                            <button
                                type="button"
                                onClick={this.handleReload}
                                style={{
                                    cursor: 'pointer',
                                    border: 'none',
                                    background: '#2563eb',
                                    color: '#fff',
                                    borderRadius: '0.5rem',
                                    padding: '0.625rem 1.25rem',
                                    fontSize: '0.95rem'
                                }}
                            >
                                Reload page
                            </button>
                        </div>
                        {import.meta.env.DEV && this.state.error?.message ? (
                            <pre
                                style={{
                                    marginTop: '1.5rem',
                                    textAlign: 'left',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: '#fca5a5',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {this.state.error.message}
                            </pre>
                        ) : null}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
